/** Cornerstone `Enums.ViewportType.STACK`. */
const STACK_VIEWPORT = "stack";
const IDENTITY_DIRECTION = [1, 0, 0, 0, 1, 0, 0, 0, 1];

/** The series geometry of a stack can't be trusted, so Save must not write a header. */
export class StackGeometryError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "StackGeometryError";
  }
}

/** Two stones of a mixed legacy mask land on the same voxel. A reload can't fix it. */
export class SavedStonesOverlapError extends StackGeometryError {
  constructor() {
    super("Two saved stones land on the same voxel");
    this.name = "SavedStonesOverlapError";
  }
}

export interface SaveGeometry {
  /** [row, column, slice] mm, the order the save API takes. */
  spacing: [number, number, number];
  origin: [number, number, number];
  direction: number[];
  /** Viewport slice index → the slice index Save writes in the file. */
  toFileSlice: (slice: number) => number;
}

const SAME_SLICE = (slice: number) => slice;

/** Minimal viewport surface Save reads (Cornerstone Stack or Volume viewport). */
interface SaveViewport {
  type?: string;
  getImageData?: () => any;
  getImageIds?: () => string[];
}

/** Minimal cached-volume surface (Cornerstone `IImageVolume`). */
export interface CachedVolume {
  imageIds?: string[];
  referencedVolumeId?: string;
  spacing?: ArrayLike<number>;
  origin?: ArrayLike<number>;
  direction?: ArrayLike<number>;
}

const finite = (value: ArrayLike<number> | undefined, length: number) =>
  value?.length === length && Array.from(value).every(Number.isFinite)
    ? Array.from(value, Number)
    : null;

/**
 * Geometry Save writes for a contour's owning viewport (ENG-258).
 *
 * A volume viewport keeps its vtkImageData geometry. A stack's vtkImageData is
 * one image (slice spacing EPSILON 0.001, the displayed image's IPP as origin),
 * so a stack instead takes the frame of the source volume the segmentation
 * setup builds for the same series (`SessionVolumeMount`, legacy
 * `StackViewport`). That frame is fixed when the volume is built: for wadouri ids
 * Cornerstone first loads the real DICOM of the first, middle and last slices, so
 * it uses the real IPP whatever the sidecar holds. Stack and volume saves of one
 * series then write the same header.
 *
 * The volume sorts its slices by position; a stack lists them in series order,
 * which may run the other way. The header is always the volume's, and
 * `toFileSlice` maps the stack's slice index onto the volume's, so every save of
 * a series writes the same header and the same voxel slice order (index-space
 * readers stay right). Any order other than the volume's or its reverse, or no
 * source volume, throws `StackGeometryError`.
 */
export function saveGeometryForViewport(
  viewport: SaveViewport,
  getVolumes: () => CachedVolume[],
): SaveGeometry {
  const imageData = viewport.getImageData?.();
  if (viewport.type !== STACK_VIEWPORT) {
    const vtk = imageData?.imageData;
    const vtkOrigin = vtk?.getOrigin?.() ?? [0, 0, 0];
    return {
      spacing: [
        imageData?.spacing?.[1] ?? 1,
        imageData?.spacing?.[0] ?? 1,
        imageData?.spacing?.[2] ?? 1,
      ],
      origin: [vtkOrigin[0], vtkOrigin[1], vtkOrigin[2]],
      direction: Array.from(vtk?.getDirection?.() ?? IDENTITY_DIRECTION, Number),
      toFileSlice: SAME_SLICE,
    };
  }

  const frame = stackVolumeFrame(viewport, getVolumes);
  return {
    spacing: [frame.spacing[1], frame.spacing[0], frame.spacing[2]],
    origin: frame.origin,
    direction: frame.direction,
    toFileSlice: frame.toFileSlice,
  };
}

/** A stack's source-volume frame (the volume's own header) and its slice mapping. */
export interface StackVolumeFrame {
  /** [column, row, slice] mm, vtk order. */
  spacing: [number, number, number];
  origin: [number, number, number];
  direction: number[];
  /** Stack slice index → volume (= file) slice index: `i` or `n - 1 - i`. */
  toFileSlice: (slice: number) => number;
}

/** The cached source volume whose images are exactly these (derived volumes skipped). */
export function findSourceVolume(
  imageIds: string[],
  getVolumes: () => CachedVolume[],
): CachedVolume | undefined {
  const n = imageIds.length;
  if (!n) return undefined;
  const wanted = new Set(imageIds);
  return getVolumes().find(
    (v) =>
      !v?.referencedVolumeId &&
      v?.imageIds?.length === n &&
      v.imageIds.every((id) => wanted.has(id)),
  );
}

/** The source volume for these images and its validated header. Throws `StackGeometryError`. */
function sourceVolumeHeader(imageIds: string[], getVolumes: () => CachedVolume[]) {
  const volume = findSourceVolume(imageIds, getVolumes);
  if (!volume) throw new StackGeometryError("No source volume for this stack");
  const spacing = finite(volume.spacing, 3);
  const origin = finite(volume.origin, 3);
  const direction = finite(volume.direction, 9);
  if (!spacing || !origin || !direction || spacing.some((v) => v <= 0)) {
    throw new StackGeometryError("Source volume has missing or invalid geometry");
  }
  return { volume, spacing, origin, direction };
}

/**
 * The source volume built for this stack's series: its header unchanged, plus
 * the map from the stack's slice index to the volume's (same order, or
 * reversed). Throws `StackGeometryError` without a source volume or for any
 * other order.
 */
export function stackVolumeFrame(
  viewport: SaveViewport,
  getVolumes: () => CachedVolume[],
): StackVolumeFrame {
  const stackIds = viewport.getImageIds?.() ?? [];
  const n = stackIds.length;
  const { volume, spacing, origin, direction } = sourceVolumeHeader(stackIds, getVolumes);

  const volumeIndex = new Map(volume.imageIds!.map((id, j) => [id, j]));
  const order = stackIds.map((id) => volumeIndex.get(id));
  const same = order.every((j, i) => j === i);
  const reversed = !same && order.every((j, i) => j === n - 1 - i);
  if (!same && !reversed) {
    throw new StackGeometryError("Stack order doesn't match its source volume");
  }

  return {
    spacing: [spacing[0], spacing[1], spacing[2]],
    origin: [origin[0], origin[1], origin[2]],
    direction,
    toFileSlice: reversed ? (slice) => n - 1 - slice : SAME_SLICE,
  };
}

/**
 * The stack's source-volume frame as a vtkImageData-like object, so the saved
 * mask loader can build per-slice planes with `volumeSlicePlane` exactly as it
 * does on a volume viewport. Its slices are in the file's (volume's) order;
 * `toFileSlice` maps a stack slice onto them.
 */
export function stackSliceFrame(viewport: SaveViewport, getVolumes: () => CachedVolume[]) {
  const frame = stackVolumeFrame(viewport, getVolumes);
  const dims = viewport.getImageData?.()?.dimensions;
  const n = viewport.getImageIds?.()?.length ?? 0;
  return {
    getOrigin: () => frame.origin,
    getDirection: () => frame.direction,
    getSpacing: () => frame.spacing,
    getDimensions: () => [dims?.[0] ?? 0, dims?.[1] ?? 0, n],
    toFileSlice: frame.toFileSlice,
  };
}

export interface LegacyStackMaskInput {
  /** Labels as stored: (rows, columns, slices), row fastest. */
  data: Uint16Array;
  rows: number;
  columns: number;
  slices: number;
  /** The series in the order the old stack listed it: stored slice s is image s. */
  listImageIds: string[];
  getVolumes: () => CachedVolume[];
  /** A label's slice order, when its record knows it (a file can mix stack and
   *  volume saves). Other labels use `defaultOrder`. */
  orderOfLabel?: (label: number) => StoredSliceOrder | undefined;
  /** Default: `list`, every label saved from the stack. */
  defaultOrder?: StoredSliceOrder;
}

/** `list`: stored slice s is `listImageIds[s]`. `volume`: already in volume order. */
export type StoredSliceOrder = "list" | "volume";

/**
 * Fix on read for a mask saved from a stack before ENG-258 (slice axis 0.001,
 * origin = the slice on screen). Its voxels are right by the stack's list index,
 * so rebuild in memory what a save writes now: each stored slice moved to its
 * image's slice in the source volume, under the volume's own header (as a file
 * affine, `_build_affine` convention). The normal loader path then reads it.
 * In a file that mixes stack and volume saves, only `list` labels move.
 * Throws `StackGeometryError` instead of guessing: no source volume, a file
 * that isn't one slice per series image (grid grown, other series), or a list
 * stone and a volume stone that land on the same voxel.
 */
export function normalizeLegacyStackMask({
  data,
  rows,
  columns,
  slices,
  listImageIds,
  getVolumes,
  orderOfLabel,
  defaultOrder = "list",
}: LegacyStackMaskInput): { data: Uint16Array; affine: number[] } {
  if (slices !== listImageIds.length) {
    throw new StackGeometryError("Saved 2D-mode mask doesn't match its series");
  }
  const { volume, spacing, origin, direction } = sourceVolumeHeader(listImageIds, getVolumes);
  const volumeIndex = new Map(volume.imageIds!.map((id, j) => [id, j]));
  const plane = rows * columns;
  const out = new Uint16Array(data.length);
  const isList = new Map<number, boolean>();
  listImageIds.forEach((id, s) => {
    const listSlice = volumeIndex.get(id)! * plane;
    const storedSlice = s * plane;
    for (let p = 0; p < plane; p++) {
      const label = data[storedSlice + p];
      if (!label) continue;
      let list = isList.get(label);
      if (list === undefined) {
        list = (orderOfLabel?.(label) ?? defaultOrder) === "list";
        isList.set(label, list);
      }
      const at = (list ? listSlice : storedSlice) + p;
      // A list stone moved onto a volume stone's voxel: one would be lost silently.
      if (out[at] && out[at] !== label) {
        throw new SavedStonesOverlapError();
      }
      out[at] = label;
    }
  });
  // File affine rows (x, y, z): [i·Δcol, j·Δrow, k·Δslice, origin], like Save + _build_affine.
  const [colSpacing, rowSpacing, sliceSpacing] = spacing;
  const affine = [0, 1, 2].flatMap((axis) => [
    direction[axis] * colSpacing,
    direction[3 + axis] * rowSpacing,
    direction[6 + axis] * sliceSpacing,
    origin[axis],
  ]);
  return { data: out, affine };
}
